import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Download, Calendar, Tag, User, Eye, Monitor, ArrowLeft, FileDown, Package, Info, Wrench } from 'lucide-react';
import { useData } from '../context/DataContext';
import ArticleRenderer from '../components/ArticleRenderer';
import ApiService from '../services/api';

const ToolDetailPage = () => {
  const { id } = useParams();
  const { tools, trackPageView } = useData();
  const [downloading, setDownloading] = useState(false);
  const [downloadCount, setDownloadCount] = useState(0);
  
  const tool = tools.find(t => t.id === id);

  useEffect(() => {
    if (tool) {
      trackPageView(`tool-${tool.id}`);
      setDownloadCount(parseInt(tool.downloads) || 0);
    }
  }, [tool, trackPageView]);

  if (!tool) {
    return <Navigate to="/tools" replace />;
  }

  const handleDownload = async () => {
    if (!tool.file_url) {
      alert('No download file available for this tool.');
      return;
    }

    setDownloading(true);
    try {
      // Track the download
      await ApiService.trackToolDownload(tool.id);
      setDownloadCount(prev => prev + 1);
      
      // Trigger the file download
      const link = document.createElement('a');
      link.href = tool.file_url;
      link.download = tool.file_name || 'download';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      // Still try to download even if tracking fails
      window.open(tool.file_url, '_blank');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="page-container">
      {/* Back Button */}
      <Link to="/tools" className="tool-back-link" style={{ 
        display: 'inline-flex', alignItems: 'center', gap: '6px', 
        textDecoration: 'none', marginBottom: '20px',
        fontSize: '0.95rem', fontWeight: '500'
      }}>
        <ArrowLeft size={18} />
        Back to Tools & Utilities
      </Link>

      {/* Tool Header */}
      <div className="tool-header-gradient" style={{ 
        background: 'linear-gradient(135deg, #17a2b8 0%, #138496 100%)', 
        borderRadius: '16px', padding: '30px', marginBottom: '25px', color: 'white' 
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
              <Wrench size={32} />
              <h1 style={{ margin: 0, fontSize: '1.8rem' }}>{tool.title}</h1>
            </div>
            <p style={{ opacity: 0.9, fontSize: '1.05rem', lineHeight: '1.6', marginBottom: '15px' }}>
              {tool.description}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', fontSize: '0.9rem', opacity: 0.85 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Tag size={16} />
                {tool.category}{tool.subcategory ? ` - ${tool.subcategory}` : ''}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <User size={16} />
                {tool.author || 'LYSH DBA Team'}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Monitor size={16} />
                {tool.platform || 'Windows'}
              </span>
              {tool.version && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Package size={16} />
                  Version {tool.version}
                </span>
              )}
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Calendar size={16} />
                {new Date(tool.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>

          {/* Download Panel */}
          <div style={{ 
            background: 'rgba(255,255,255,0.15)', borderRadius: '12px', 
            padding: '20px', minWidth: '220px', textAlign: 'center',
            backdropFilter: 'blur(10px)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '15px' }}>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{downloadCount}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Downloads</div>
              </div>
              <div>
                <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>{tool.views || 0}</div>
                <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Views</div>
              </div>
            </div>

            {tool.file_url ? (
              <button 
                onClick={handleDownload} 
                disabled={downloading}
                className="tool-download-btn"
                style={{ 
                  border: 'none', padding: '12px 30px', borderRadius: '8px', 
                  fontSize: '1rem', fontWeight: '600', 
                  cursor: downloading ? 'wait' : 'pointer',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  width: '100%', justifyContent: 'center',
                  opacity: downloading ? 0.7 : 1,
                  transition: 'all 0.2s'
                }}
              >
                <FileDown size={20} />
                {downloading ? 'Downloading...' : 'Download Tool'}
              </button>
            ) : (
              <div style={{ 
                background: 'rgba(255,255,255,0.1)', padding: '12px',
                borderRadius: '8px', fontSize: '0.85rem', opacity: 0.7
              }}>
                No download file attached
              </div>
            )}

            {tool.file_name && (
              <div style={{ marginTop: '10px', fontSize: '0.8rem', opacity: 0.7 }}>
                <div>{tool.file_name}</div>
                {tool.file_size && <div>{tool.file_size}</div>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tool Content / Instructions */}
      <div className="section-card tool-content-section" style={{ 
        padding: '30px', marginBottom: '25px' 
      }}>
        <h2 className="tool-content-heading" style={{ 
          display: 'flex', alignItems: 'center', gap: '10px', 
          marginBottom: '20px', fontSize: '1.4rem' 
        }}>
          <Info size={24} color="#17a2b8" />
          Tool Information & Instructions
        </h2>
        
        {tool.content ? (
          typeof tool.content === 'string' ? (
            <div className="article-body">
              {tool.content.split('\n').map((paragraph, index) => (
                <p key={index} className="tool-content-text" style={{ marginBottom: '15px', lineHeight: '1.7' }}>
                  {paragraph}
                </p>
              ))}
            </div>
          ) : (
            <ArticleRenderer content={tool.content} />
          )
        ) : (
          <p className="tool-empty-text" style={{ fontStyle: 'italic' }}>
            No detailed instructions available for this tool.
          </p>
        )}
      </div>

      {/* Download Section (Bottom) */}
      {tool.file_url && (
        <div className="tool-download-section" style={{ 
          borderRadius: '12px', padding: '25px', 
          textAlign: 'center', marginBottom: '25px' 
        }}>
          <Download size={36} color="#17a2b8" style={{ marginBottom: '10px' }} />
          <h3 className="tool-content-heading" style={{ marginBottom: '8px' }}>Ready to Download?</h3>
          <p className="tool-card-description" style={{ marginBottom: '15px', fontSize: '0.95rem' }}>
            {tool.file_name && <span><strong>File:</strong> {tool.file_name}</span>}
            {tool.file_size && <span> &middot; <strong>Size:</strong> {tool.file_size}</span>}
            {tool.platform && <span> &middot; <strong>Platform:</strong> {tool.platform}</span>}
          </p>
          <button 
            onClick={handleDownload}
            disabled={downloading}
            className="btn btn-primary"
            style={{ 
              padding: '12px 40px', fontSize: '1.05rem', 
              background: '#17a2b8', borderColor: '#17a2b8',
              display: 'inline-flex', alignItems: 'center', gap: '8px'
            }}
          >
            <FileDown size={20} />
            {downloading ? 'Downloading...' : 'Download Now'}
          </button>
        </div>
      )}

      {/* Usage Notes */}
      <div className="tool-notes-section" style={{ padding: '20px', borderRadius: '8px' }}>
        <h3 className="tool-notes-heading" style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Info size={20} />
          Important Notes
        </h3>
        <ul className="tool-notes-list" style={{ lineHeight: '1.8', margin: 0, paddingLeft: '20px' }}>
          <li>Always scan downloaded files with your antivirus software before running</li>
          <li>Test tools in a non-production environment first</li>
          <li>Review the documentation and instructions before using the tool</li>
          <li>Ensure your system meets the minimum requirements for the tool</li>
          <li>Keep backups of your data before running any utility</li>
          <li>Check for updates regularly for the latest features and fixes</li>
        </ul>
      </div>
    </div>
  );
};

export default ToolDetailPage;
